import DOMPurify from "dompurify"

/**
 * HTML входящего письма приходит из чужой почты: перед показом его очищаем
 * (скрипты, обработчики событий, формы, iframe) и отделяем цитату прошлой
 * переписки, которую почтовые клиенты добавляют в ответ.
 */

const QUOTE_SELECTORS = [
  // Gmail
  ".gmail_quote",
  // Outlook
  "#appendonsend",
  "#divRplyFwdMsg",
  // Яндекс, Mail.ru, Apple Mail и большинство клиентов
  "blockquote",
]

export interface MailHtml {
  /** Собственный текст письма */
  body: string
  /** Цитата прошлой переписки (пусто, если не нашли) */
  quote: string
}

export const sanitizeMailHtml = (html: string): string =>
  DOMPurify.sanitize(html ?? "", {
    FORBID_TAGS: ["style", "form", "input", "button", "textarea", "select", "iframe", "object", "embed"],
    FORBID_ATTR: ["style"],
    // ссылки из письма открываем в новой вкладке
    ADD_ATTR: ["target"],
  })

/**
 * Очистить HTML и разделить на текст письма и цитату. Цитатой считается первый
 * найденный блок цитаты и всё, что после него.
 */
export const splitMailHtml = (html: string): MailHtml => {
  const clean = sanitizeMailHtml(html)
  const container = document.createElement("div")
  container.innerHTML = clean

  // встроенные картинки письма (cid:) браузер загрузить не может - файлы и так
  // показаны вложениями
  container.querySelectorAll('img[src^="cid:"]').forEach((image) => image.remove())

  container.querySelectorAll("a[href]").forEach((link) => {
    link.setAttribute("target", "_blank")
    link.setAttribute("rel", "noopener noreferrer")
  })

  const quote = QUOTE_SELECTORS.map((selector) => container.querySelector(selector)).find(
    (node): node is Element => !!node
  )

  if (!quote) {
    return { body: container.innerHTML, quote: "" }
  }

  // всё, что после цитаты, тоже относится к старой переписке
  const range = document.createRange()
  range.setStartBefore(quote)
  range.setEndAfter(container.lastChild ?? quote)
  const fragment = range.extractContents()

  const holder = document.createElement("div")
  holder.appendChild(fragment)

  // строка «… написал:» перед цитатой — тоже часть цитаты
  const last = container.lastElementChild
  if (last && /(написал|wrote)\s*:?\s*$/i.test(last.textContent ?? "")) {
    holder.insertBefore(last, holder.firstChild)
  }

  const body = container.innerHTML.trim()

  // письмо целиком из цитаты (переслали без комментария) — показываем как есть
  if (!container.textContent?.trim()) {
    return { body: holder.innerHTML, quote: "" }
  }

  return { body, quote: holder.innerHTML }
}
